// Label geometry for the CMS Pre-Delivering ZPL labels.
//
// Coordinates below are ZPL dots for the 95 x 45 mm label stock.
//
// Each template exposes `label1` and `label2`, printed in that order. label2 omits the
// product/footer section by design.
//
// Element kinds:
//   rule  — a horizontal divider (^GB)
//   box   — an outlined rectangle (^GB)
//   text  — a static caption; the words are part of the design, not data
//   field — a data-bound value, keyed into the object resolveMarketplaceLabelData returns
//   group — elements printed only when `requires` resolves to a non-empty value
//
// `w` is the field block width used for alignment (^FB); `align` is L / C / R.

// ── Stock ────────────────────────────────────────────────────────────────────
// The reference ZebraDesigner file "kick avenue 95x45.prn" uses 759 x 360 dots at 8dpmm.
export const LABEL_PW = 759;
export const LABEL_LL = 360;

const font = size => ({ h: size, w: size });
const rule = (x, y, w, thickness) => ({ kind: 'rule', x, y, w, thickness });
const box = (x, y, w, h, thickness) => ({ kind: 'box', x, y, w, h, thickness });
const text = (x, y, w, fontStyle, label, align = 'L') => ({
  kind: 'text',
  x,
  y,
  w,
  font: fontStyle,
  align,
  text: label,
});
const field = (x, y, w, fontStyle, key, options = {}) => ({
  kind: 'field',
  x,
  y,
  w,
  font: fontStyle,
  align: 'L',
  key,
  ...options,
});

// Zebra font 0 has no regular/bold weight switch, so titles use a smaller square size to
// create a visibly lighter hierarchy than their values.
const KA_FONT = {
  brand: font(42),
};

// Marketplace labels share one baseline layout. The upper section is intentionally value-only:
// the label order itself is the visual cue (date, KA order id, marketplace order id, AWB, CG ID).
// Shopee reserves the right column for its optional pickup panel.
const MARKETPLACE_FONT = {
  value: font(34),
  brand: font(42),
  pickupCaption: font(22),
  pickupValue: font(80),
};

const kaHeader = [
  text(6, 18, 750, KA_FONT.brand, 'KICK AVENUE EXPRESS', 'C'),
];

const kaBodyBase = [
  field(6, 68, 750, MARKETPLACE_FONT.value, 'orderDate', { align: 'C', fit: true }),
  field(6, 108, 750, MARKETPLACE_FONT.value, 'invoiceNumber', { align: 'C', fit: true }),
];
const kaCgBlock = {
  kind: 'group',
  requires: 'showCgId',
  elements: [field(6, 148, 750, MARKETPLACE_FONT.value, 'cgId', { align: 'C', fit: true })],
};
const kaBody = [
  ...kaBodyBase,
  rule(6, 142, 750, 2),
  kaCgBlock,
];
const kaBodyWithoutCg = kaBodyBase;

const marketplaceHeader = (brand) => [
  text(6, 18, 750, MARKETPLACE_FONT.brand, brand, 'C'),
];

const marketplaceBodyBase = [
  field(6, 68, 750, MARKETPLACE_FONT.value, 'orderDate', { align: 'C', fit: true }),
  field(6, 108, 750, MARKETPLACE_FONT.value, 'invoiceNumber', { align: 'C', fit: true }),
  field(6, 148, 750, MARKETPLACE_FONT.value, 'marketplaceOrderId', { align: 'C', fit: true }),
  field(6, 188, 750, MARKETPLACE_FONT.value, 'awb', { align: 'C', fit: true }),
];
const marketplaceCgBlock = {
  kind: 'group',
  requires: 'showCgId',
  elements: [field(6, 228, 750, MARKETPLACE_FONT.value, 'cgId', { align: 'C', fit: true })],
};
const marketplaceBody = [
  ...marketplaceBodyBase,
  rule(6, 222, 750, 2),
  marketplaceCgBlock,
];
const marketplaceBodyWithoutCg = marketplaceBodyBase;

// Shopee keeps the existing pickup panel on the right, so its four values use the left
// column while retaining the same value-only order and larger font treatment.
const marketplaceBodyWithPickupBase = [
  field(6, 68, 460, MARKETPLACE_FONT.value, 'orderDate', { align: 'C', fit: true }),
  field(6, 108, 460, MARKETPLACE_FONT.value, 'invoiceNumber', { align: 'C', fit: true }),
  field(6, 148, 460, MARKETPLACE_FONT.value, 'marketplaceOrderId', { align: 'C', fit: true }),
  field(6, 188, 460, MARKETPLACE_FONT.value, 'awb', { align: 'C', fit: true }),
];
const marketplaceCgBlockWithPickup = {
  kind: 'group',
  requires: 'showCgId',
  elements: [field(6, 228, 460, MARKETPLACE_FONT.value, 'cgId', { align: 'C', fit: true })],
};
const marketplaceBodyWithPickup = [
  ...marketplaceBodyWithPickupBase,
  rule(6, 222, 460, 2),
  marketplaceCgBlockWithPickup,
];
const marketplaceBodyWithPickupWithoutCg = marketplaceBodyWithPickupBase;

// The whole pickup panel disappears when no code exists; the logistics column never grows
// into this area, so the same layout remains safe when a code arrives on another sale.
const marketplacePickupBlock = {
  kind: 'group',
  requires: 'pickupCode',
  elements: [
    box(490, 82, 242, 180, 3),
    text(490, 113, 242, MARKETPLACE_FONT.pickupCaption, 'Kode Pickup (Instan)', 'C'),
    field(490, 151, 242, MARKETPLACE_FONT.pickupValue, 'pickupCode', { align: 'C' }),
  ],
};

const marketplaceProductBand = (width = 750, skuY = 268, sizeRackY = 308) => [
  field(6, skuY, width, MARKETPLACE_FONT.value, 'sku', { align: 'C', fit: true }),
  field(6, sizeRackY, width, MARKETPLACE_FONT.value, 'sizeRack', { align: 'C', fit: true }),
];

// KA/Store have a shorter body, so keep the product values close to CG ID instead of leaving
// the marketplace footer at its lower baseline.
const kaProductBand = marketplaceProductBand(750, 190, 230);

// KA/Store do not have marketplace order ids or AWBs, so their body omits those two fields.
export const KA = {
  label1: [...kaHeader, ...kaBody, ...kaProductBand],
  label2: [...kaHeader, ...kaBodyWithoutCg],
};

// TikTok and Shopee share the same marketplace baseline; Shopee adds pickup conditionally.
export const TIKTOK = {
  label1: [...marketplaceHeader('TIKTOK SHOP'), ...marketplaceBody, ...marketplaceProductBand()],
  label2: [...marketplaceHeader('TIKTOK SHOP'), ...marketplaceBodyWithoutCg],
};

export const SHOPEE = {
  label1: [...marketplaceHeader('SHOPEE'), ...marketplaceBodyWithPickup, marketplacePickupBlock, ...marketplaceProductBand(460)],
  label2: [...marketplaceHeader('SHOPEE'), ...marketplaceBodyWithPickupWithoutCg, marketplacePickupBlock],
};
